<template>
  <span
    class="merge-conflict-value"
    :class="{
      'merge-conflict-value_inline': inline || value.status !== 'present',
    }"
    :style="SEMANTIC_COLOR_VARIABLES"
  >
    <span
      v-if="value.status === 'removed'"
      class="merge-conflict-value__removed"
    >
      <i
        class="pi pi-trash merge-conflict-value__icon"
        aria-hidden="true"
      />
      {{ t('removed') }}
    </span>
    <span
      v-else-if="value.status === 'missing'"
      class="merge-conflict-value__missing"
    >
      {{ t('missing') }}
    </span>
    <template v-else>
      <span
        v-for="(line, lineIndex) in value.lines"
        :key="lineIndex"
        class="merge-conflict-value__line"
      >
        <span
          v-for="(part, partIndex) in line"
          :key="partIndex"
          :class="{
            'merge-conflict-value__part_changed': part.isChanged,
          }"
        >{{ part.text }}</span>
      </span>
    </template>
  </span>
</template>

<script setup lang="ts">
import {
  IMergeConflictValueView,
} from '../../composable/use-merge-conflict.ts';
import {
  SemanticColors,
} from '../../constants/color.ts';
import useLibraryI18n from '../../i18n/composable.ts';

defineProps<{
  value: IMergeConflictValueView,
  /** Shows list items on one line, separated by commas. */
  inline?: boolean,
}>();

const {
  t,
} = useLibraryI18n('components/modal-merge-conflict/modal-merge-conflict');

// A change made in the meantime is something to be aware of, not a success or a failure.
const SEMANTIC_COLOR_VARIABLES = {
  '--merge-conflict-value-change-color': SemanticColors.informational,
};
</script>

<style lang="scss" scoped>
.merge-conflict-value {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  overflow-wrap: anywhere;

  &_inline {
    display: inline;

    .merge-conflict-value__line:not(:last-child)::after {
      content: ', ';
    }
  }

  &__removed {
    display: inline-flex;
    align-items: center;
    gap: var(--gap-union);
  }

  &__icon {
    color: var(--merge-conflict-value-change-color);
  }

  &__missing {
    font-style: italic;
    color: var(--text-secondary-color);
  }

  &__part_changed {
    border-radius: var(--border-radius-small);
    background-color: color-mix(in srgb, var(--merge-conflict-value-change-color) 20%, transparent);
  }
}
</style>
